import React from 'react';
import { GraduationCap, Heart, BookOpen, Scale, Quote, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Layout from '../components/layout/Layout';
import { aboutContent, doctorInfo, affiliations } from '../data/mock';

const AboutPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 medical-pattern opacity-50" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              <span>Parcours et valeurs</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              À propos
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              Découvrez mon parcours, ma vision de la médecine et les valeurs qui guident ma pratique chirurgicale.
            </p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="animate-fade-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-teal-700" />
                </div>
                <h2 className="text-2xl font-medium text-gray-900">
                  {aboutContent.vision.title}
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed text-lg">
                {aboutContent.vision.text}
              </p>
            </div>
            
            <div className="animate-fade-in-up animate-delay-200">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-700 to-teal-800 text-white overflow-hidden">
                <CardContent className="p-8 relative">
                  <Quote className="absolute top-4 right-4 w-16 h-16 text-white/10" />
                  <blockquote className="text-xl leading-relaxed relative z-10">
                    "{doctorInfo.tagline}"
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-sm font-medium">LK</span>
                    </div>
                    <div>
                      <p className="font-medium">{doctorInfo.name}</p>
                      <p className="text-sm text-teal-200">Chirurgien</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* Background Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-teal-700" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900">
                {aboutContent.background.title}
              </h2>
            </div>
            
            <div className="space-y-4">
              {aboutContent.background.items.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-teal-50 transition-colors group"
                >
                  <span className="w-8 h-8 rounded-lg bg-white shadow-sm text-teal-700 flex items-center justify-center text-sm font-semibold flex-shrink-0 group-hover:bg-teal-700 group-hover:text-white transition-colors">
                    {index + 1}
                  </span>
                  <span className="text-gray-700 pt-1">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Education & Ethics Grid */}
      <section className="py-16 bg-gray-50 medical-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Education Card */}
            <Card className="border-0 shadow-sm bg-white hover-lift">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-teal-700" />
                  </div>
                  <h2 className="text-xl font-medium text-gray-900">
                    {aboutContent.education.title}
                  </h2>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {aboutContent.education.text}
                </p>
              </CardContent>
            </Card>

            {/* Ethics Card */}
            <Card className="border-0 shadow-sm bg-white hover-lift">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <Scale className="w-6 h-6 text-teal-700" />
                  </div>
                  <h2 className="text-xl font-medium text-gray-900">
                    {aboutContent.ethics.title}
                  </h2>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {aboutContent.ethics.text}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Affiliations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-medium text-gray-900 mb-8">
              Affiliations et engagements
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {affiliations.map((affiliation, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 text-left"
                >
                  <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{affiliation}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
